import React, { useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { CssBaseline } from "@material-ui/core";
import { styled } from '@mui/material/styles';
import "./Dashboard.css";
import Header from "./Header";
import {
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  TablePagination,
  Select,
  MenuItem,
  Box,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";


const AntTabs = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#1890ff',
  },
});
const isDev = process.env.REACT_APP_ENV === "development";

const AntTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(1),
    color: 'rgba(0, 0, 0, 0.85)',
    fontSize: '1rem',
    fontWeight: 'bold',
    // fontFamily: [
    //   '-apple-system',
    //   'BlinkMacSystemFont',
    //   '"Segoe UI"',
    //   'Roboto',
    //   '"Helvetica Neue"',
    //   'Arial',
    //   'sans-serif',
    //   '"Apple Color Emoji"',
    //   '"Segoe UI Emoji"',
    //   '"Segoe UI Symbol"',
    // ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
      fontSize: '1.2rem',
      fontWeight: 'bold'
    },
    '&.Mui-selected': {
      color: '#1890ff',
      fontWeight: theme.typography.fontWeightMedium,
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#d1eaff',
    },
  }),
);

const ExpandableCell = ({ files }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="outlined"
        size="small"
      >
        {isExpanded ? "Hide" : `Show (${files.length})`}
      </Button>
      {isExpanded && (
        <ul style={{ paddingLeft: "20px", marginTop: "5px" }}>
          {files.map((file, index) => (
            <li key={index}>{file}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Dashboard = ({ classes, username, fetchAPI }) => {
  const [data, setData] = useState([]);
  const [downloadData, setDownloadData] = useState([]);
  const [ahriData, setAhriData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadingContext, setLoadingContext] = useState(null);
  const [ahriPage, setAhriPage] = useState(0);
  const [ahriRowsPerPage, setAhriRowsPerPage] = useState(10);

  // Function to update status using contextID
  const ahriupdateStatus = async (contextID) => {
    setLoadingContext(contextID);
    try {
      // Simulate API call to fetch new status
      const newStatus = await fetchStatusFromAPI(contextID);

      // Update the status in state
      setAhriData(prevData =>
        prevData.map(item =>
          item.contextID === contextID ? { ...item, status: newStatus } : item
        )
      );
    } catch (error) {
      console.error("Error fetching status:", error);
    }
    setLoadingContext(null);
  };

  const fetchStatusFromAPI = async (contextID, retries = 3, delay = 1000) => {
    try {
      let prefix = { contextID: contextID };
      const response = await fetchAPI.post("/ahriuploadstatus", prefix, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });

      console.log("Raw Response:", response.data);

      if (response.data && response.data.status) {
        return response.data.status;
      } else {
        throw new Error("Invalid API response: Missing 'status' field");
      }
    } catch (error) {
      if (error.response && error.response.status === 504 && retries > 0) {
        console.warn(`504 Error: Retrying in ${delay / 1000} seconds...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchStatusFromAPI(contextID, retries - 1, delay * 2); // Exponential backoff
      }

      console.error("Final error after retries:", error);
      return "Error"; // Return error status after all retries fail
    }
  };


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (newValue === 1 && downloadData.length === 0) {
      fetchDownloadData();
    }
  };

  useEffect(() => {
    fetchData();
    fetchAHRIData();
  }, []);


  useEffect(() => {
    console.log("modified", ahriData)
  }, [ahriData]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchAPI.get("/uploads", {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });
      if (Array.isArray(response.data)) {
        setData(response.data);
      } else {
        console.error("Data returned is not an array:", response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };


  const fetchAHRIData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchAPI.get("/ahriupload", {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });
  
      const data = response.data?.data || [];
      setAhriData(data);
      console.log("Fetched Data:", data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDownloadData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchAPI.get("/downloads", {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
      });
      if (Array.isArray(response.data)) {
        setDownloadData(response.data);
      } else {
        console.error("Download history returned is not an array:", response.data);
      }
    } catch (error) {
      console.error("Error fetching download history:", error);
    }
    setIsLoading(false);
  };

  const handleHistoryDownload = async (recordId) => {
    try {
      const response = await fetchAPI.get(`/download_file/${recordId}`, {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${localStorage.getItem("msal.idtoken")}`,
        },
        responseType: "blob",
      });

      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const responseText = await response.data.text();
        const responseData = JSON.parse(responseText);
        const fileUrl = responseData && responseData.presigned_url;
        if (fileUrl) {
          window.location.assign(fileUrl);
        } else {
          console.error("No download url returned for record:", recordId);
        }
        return;
      }

      const downloadUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const contentDisposition = response.headers["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|\")?([^;\"]+)/i);
      const fallbackName = `download-history-file-${Date.now()}`;
      link.download = filenameMatch ? decodeURIComponent(filenameMatch[1]) : fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error generating download link:", error);
    }
  };
  

  const columns = React.useMemo(
    () => [
      {
        Header: "Failed Files",
        accessor: "failed_files",
        Cell: ({ cell: { value } }) => <ExpandableCell files={value || []} />,
      },
      {
        Header: "Successful Files",
        accessor: "successful_files",
        Cell: ({ cell: { value } }) => <ExpandableCell files={value || []} />,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span
            style={{
              color:
                value === "SUCCESS"
                  ? "green"
                  : value === "FAILED"
                    ? "red"
                    : "orange",
              fontWeight: "bold",
            }}
          >
            {value || "N/A"}
          </span>
        ),
      },
      {
        Header: "Uploaded By",
        accessor: "uploaded_by",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
      {
        Header: "Uploaded On",
        accessor: "uploaded_on",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
    ],
    []
  );

  const downloadColumns = React.useMemo(
    () => [
      {
        Header: "Request Type",
        accessor: "request_type",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
      {
        Header: "Selected Units",
        accessor: "selected_units",
        Cell: ({ cell: { value } }) => {
          if (!value || typeof value !== "object") return "N/A";
          const allUnits = Object.values(value)
            .flatMap((item) => (Array.isArray(item) ? item : [item]))
            .filter(Boolean);
          return allUnits.length ? allUnits.join(", ") : "N/A";
        },
      },
      {
        Header: "Requested User",
        accessor: "requested_by",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
      {
        Header: "Downloaded On",
        accessor: "requested_on",
        Cell: ({ cell: { value } }) => value || "Unknown",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span
            style={{
              color: value === "SUCCESS" ? "green" : value === "FAILED" ? "red" : "orange",
              fontWeight: "bold",
            }}
          >
            {value || "Unknown"}
          </span>
        ),
      },
      {
        Header: "Download",
        id: "download",
        Cell: ({ row }) => {
          const canDownload = row.original.status === "SUCCESS" && row.original.file_key;
          return (
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={!canDownload}
              onClick={() => handleHistoryDownload(row.original._id)}
            >
              Download
            </Button>
          );
        },
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize },
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10 },
    },
    usePagination
  );

  const {
    getTableProps: getDownloadTableProps,
    getTableBodyProps: getDownloadTableBodyProps,
    headerGroups: downloadHeaderGroups,
    page: downloadPage,
    pageCount: downloadPageCount,
    gotoPage: gotoDownloadPage,
    setPageSize: setDownloadPageSize,
    state: { pageIndex: downloadPageIndex, pageSize: downloadPageSize },
    prepareRow: prepareDownloadRow,
  } = useTable(
    {
      columns: downloadColumns,
      data: downloadData,
      initialState: { pageSize: 10 },
    },
    usePagination
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <Card className={classes.card}>
        <Header
          position="static"
          className={classes.appbar}
          username={username}
        />

        <CardContent>
          {/* Tabs Section */}
          <AntTabs
            value={selectedTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
          >
            <AntTab label="UPLOAD HISTORY" />
            {isDev ? <AntTab label="DOWNLOAD HISTORY" /> : null}
            {isDev ? <AntTab label="AHRI UPLOAD HISTORY" /> : null}
          </AntTabs>
          {selectedTab === 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem",
                }}
              >
                <Typography variant="h6" className={classes.btn}>
                  Upload History
                </Typography>
                <IconButton
                  onClick={fetchData}
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </div>
              <table {...getTableProps()} className="table">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps()}
                          style={{ backgroundColor: "navy", color: "white" }}
                        >
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 2,
                  px: 1,
                  py: 1,
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                  borderRadius: "0 0 6px 6px",
                }}
              >
                {/* Rows per page selector */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <Select
                    size="small"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      gotoPage(0);
                    }}
                    sx={{ fontSize: "0.875rem", minWidth: 72 }}
                  >
                    {[5, 10, 20, 50].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Numbered pagination */}
                <Pagination
                  count={pageCount}
                  page={pageIndex + 1}
                  onChange={(_, value) => gotoPage(value - 1)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  size="medium"
                />

                {/* Page info */}
                <Typography variant="body2" color="text.secondary">
                  {data.length === 0
                    ? "0–0 of 0"
                    : `${pageIndex * pageSize + 1}–${Math.min(
                        (pageIndex + 1) * pageSize,
                        data.length
                      )} of ${data.length}`}
                </Typography>
              </Box>
              <p style={{ color: "red", font: "small-caption" }}>
                * User can see upload history without a date limit
              </p>
            </div>
          )}
          {isDev && selectedTab === 1 && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem",
                }}
              >
                <Typography variant="h6" className={classes.btn}>
                  Download History
                </Typography>
                <IconButton
                  onClick={fetchDownloadData}
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </div>
              <table {...getDownloadTableProps()} className="table download-history-table">
                <thead>
                  {downloadHeaderGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps()}
                          style={{ backgroundColor: "navy", color: "white" }}
                        >
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getDownloadTableBodyProps()}>
                  {downloadPage.map((row) => {
                    prepareDownloadRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 2,
                  px: 1,
                  py: 1,
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                  borderRadius: "0 0 6px 6px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <Select
                    size="small"
                    value={downloadPageSize}
                    onChange={(e) => {
                      setDownloadPageSize(Number(e.target.value));
                      gotoDownloadPage(0);
                    }}
                    sx={{ fontSize: "0.875rem", minWidth: 72 }}
                  >
                    {[5, 10, 20, 50].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Pagination
                  count={downloadPageCount}
                  page={downloadPageIndex + 1}
                  onChange={(_, value) => gotoDownloadPage(value - 1)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  size="medium"
                />

                <Typography variant="body2" color="text.secondary">
                  {downloadData.length === 0
                    ? "0–0 of 0"
                    : `${downloadPageIndex * downloadPageSize + 1}–${Math.min(
                        (downloadPageIndex + 1) * downloadPageSize,
                        downloadData.length
                      )} of ${downloadData.length}`}
                </Typography>
              </Box>
              <p style={{ color: "red", font: "small-caption" }}>
                * User can see download history of last 14 days
              </p>
            </div>
          )}
          {isDev && selectedTab === 2 && (
            <div style={{ padding: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "1rem",
                }}
              >
                <Typography variant="h6">AHRIUPLOAD HISTORY</Typography>
                <IconButton
                  onClick={fetchAHRIData}
                  color="primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </div>
              <TableContainer component={Paper} style={{ marginTop: "1rem" }}>
                {isLoading ? (
                  <div style={{ textAlign: "center", padding: "2rem" }}>
                    <CircularProgress />
                    <p>Loading data, please wait...</p>
                  </div>
                ) : ahriData.length === 0 ? (
                  <p style={{ textAlign: "center" }}>No data available.</p>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow style={{ backgroundColor: "navy" }}>
                        <TableCell style={{ color: "white" }}>File Name</TableCell>
                        <TableCell style={{ color: "white" }}>Uploaded At</TableCell>
                        <TableCell style={{ color: "white" }}>Status</TableCell>
                        <TableCell style={{ color: "white" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ahriData
                        .slice(
                          ahriPage * ahriRowsPerPage,
                          ahriPage * ahriRowsPerPage + ahriRowsPerPage
                        )
                        .map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.fileName}</TableCell>
                          <TableCell>{row.uploaded_at}</TableCell>
                          <TableCell
                            style={{
                              color:
                                row.upload_status === -1
                                  ? "red"
                                  : row.upload_status === 1
                                    ? "green"
                                    : "gray",
                              fontWeight: "bold",
                            }}
                          >
                            {row.upload_status === -1
                              ? "UPLOAD FAILED"
                              : row.upload_status === 1
                                ? "SUCCESS"
                                : row.status}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => ahriupdateStatus(row.contextID)}
                              disabled={
                                !row.contextID ||
                                row.upload_status === 0 ||
                                loadingContext === row.contextID ||
                                row.status === "Process completed."
                              }
                            >
                              {loadingContext === row.contextID ? (
                                <CircularProgress size={20} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
              <TablePagination
                component="div"
                count={ahriData.length}
                page={ahriPage}
                onPageChange={(_, newPage) => setAhriPage(newPage)}
                rowsPerPage={ahriRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setAhriRowsPerPage(parseInt(e.target.value, 10));
                  setAhriPage(0);
                }}
                rowsPerPageOptions={[5, 10, 20, 50]}
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                  borderRadius: "0 0 6px 6px",
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                    { fontSize: "0.875rem" },
                }}
              />

            </div>
          )}

        </CardContent>
      </Card>
    </React.Fragment>
  );
};

export default Dashboard;
